import { useRef, useState } from 'react';
import type { MediaPost } from '@/lib/media';
import { formatCount, isYoutubeUrl, incrementPostView } from '@/lib/media';
import YoutubeEmbed from '@/pages/media/components/YoutubeEmbed';

interface ArtistVideoGridProps {
  videos: MediaPost[];
}

function ArtistVideoGrid({ videos }: ArtistVideoGridProps) {
  const [active, setActive] = useState<MediaPost | null>(null);
  const countedRef = useRef<Set<string>>(new Set());

  const handlePlay = (video: MediaPost) => {
    if ((video as MediaPost & { demo?: boolean }).demo) return;
    if (countedRef.current.has(video.id)) return;
    countedRef.current.add(video.id);
    incrementPostView(video.id).catch(() => {});
  };

  if (videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-video-line text-3xl text-foreground-400" />
        </div>
        <p className="text-foreground-400">Aucune vidéo publiée par cet artiste pour le moment.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {videos.map((video) => (
          <button
            key={video.id}
            onClick={() => setActive(video)}
            className="group text-left rounded-xl overflow-hidden border border-background-200/70 bg-background-50 hover:border-background-300/70 transition-colors"
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={video.thumbnail_url || ''}
                alt={video.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 group-hover:scale-110 transition-transform">
                  <i className="ri-play-fill text-2xl" />
                </div>
              </div>
              <span className="absolute top-3 left-3 inline-block px-2.5 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs text-foreground-50">
                {video.category}
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-foreground-50 line-clamp-2 leading-snug">
                {video.title}
              </h3>
              <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {formatCount(video.views)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-heart-3-line" />
                  {formatCount(video.likes)}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Modal de lecture */}
      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background-950/80 backdrop-blur-sm"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-3xl rounded-xl overflow-hidden bg-background-950"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl" />
            </button>
            {isYoutubeUrl(active.video_url) ? (
              <div className="aspect-video">
                <YoutubeEmbed
                  url={active.video_url}
                  title={active.title}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <video
                src={active.video_url}
                poster={active.thumbnail_url || undefined}
                controls
                autoPlay
                playsInline
                onPlay={() => handlePlay(active)}
                className="w-full max-h-[70vh] bg-background-950"
              />
            )}
            <div className="p-4">
              <h3 className="font-heading font-semibold text-lg text-foreground-50">
                {active.title}
              </h3>
              {active.description && (
                <p className="text-sm text-foreground-500 mt-1">{active.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ArtistVideoGrid;